///
/// EarthBrowser
///
/// Created by Matt Giger on 9/23/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Story tours sync orchestrator. Local-first, whole-story last-write-
/// wins (cloned from savedViewsSync):
///
/// 1. GET the server delta since lastSyncedAt (tombstones included)
/// 2. Decide which local stories/deletions are newer and must be pushed
/// 3. Merge the server delta into the local slice (LWW per story)
/// 4. Push local winners (PUT) and tombstones (DELETE)
/// 5. Advance lastSyncedAt to server_time only if every push succeeded,
///    so failed pushes are retried on the next sync
///
/// On first sign-in lastSyncedAt is null, so all anonymous local stories
/// are pushed up automatically (ids are UUIDs, no merge conflicts).
///

import { recordToStory, storiesApi, storyToPutRequest } from "./storiesApi"
import { markStoriesSynced, mergeServerStories, removeStoryTombstones, type Story } from "./storiesSlice"
import { appStore } from "./store"

let syncInFlight = false

/**
 * Run a full delta sync. Safe to call at any time: no-ops when signed
 * out or when a sync is already running. Failures leave the local state
 * intact and are retried on the next trigger (sign-in, reconnect, next
 * app start).
 */
export async function syncStories(): Promise<void> {
	if (syncInFlight) return
	if (!appStore.getState().user.userProfile) return

	syncInFlight = true
	try {
		const since = appStore.getState().stories.lastSyncedAt

		// 1. Fetch the server delta
		const subscription = appStore.dispatch(
			storiesApi.endpoints.getStoriesDelta.initiate({ since }, { forceRefetch: true })
		)
		let delta
		try {
			delta = await subscription.unwrap()
		} finally {
			subscription.unsubscribe()
		}

		// 2. Decide pushes from the pre-merge snapshot
		const local = appStore.getState().stories
		const serverById = new Map(delta.stories.map(record => [record.id, record]))

		const pushes = Object.values(local.stories).filter(story => {
			const server = serverById.get(story.id)
			// Server copy (or deletion) is same-or-newer: server wins
			if (server && server.updated_at >= story.updatedAt) return false
			// Changed since the last sync, or never synced, or newer than
			// the server copy: push it up
			return since === null || story.updatedAt > since || server !== undefined
		})
		const deletions = Object.keys(local.deleted).filter(id => {
			const server = serverById.get(id)
			// Server re-created the story after our deletion: server wins
			return !(server && !server.deleted_at && server.updated_at >= local.deleted[id])
		})

		// 3. Merge the server delta locally (LWW per story)
		const merged: Story[] = []
		for (const record of delta.stories) {
			if (record.deleted_at) continue
			const story = recordToStory(record)
			if (story) merged.push(story)
		}
		appStore.dispatch(mergeServerStories({
			stories: merged,
			tombstones: delta.stories
				.filter(record => record.deleted_at)
				.map(record => ({ id: record.id, deletedAt: record.deleted_at! })),
		}))

		// 4. Push local winners
		let allPushesSucceeded = true

		for (const story of pushes) {
			try {
				await appStore.dispatch(
					storiesApi.endpoints.putStory.initiate({ id: story.id, body: storyToPutRequest(story) })
				).unwrap()
			} catch (error) {
				console.warn(`[storiesSync] Failed to push story ${story.id}:`, error)
				allPushesSucceeded = false
			}
		}

		const deletedOnServer: string[] = []
		for (const id of deletions) {
			try {
				await appStore.dispatch(storiesApi.endpoints.deleteStoryRemote.initiate(id)).unwrap()
				deletedOnServer.push(id)
			} catch (error) {
				console.warn(`[storiesSync] Failed to push deletion ${id}:`, error)
				allPushesSucceeded = false
			}
		}
		if (deletedOnServer.length > 0) {
			appStore.dispatch(removeStoryTombstones(deletedOnServer))
		}

		// 5. Advance the cursor only when everything reached the server
		if (allPushesSucceeded) {
			appStore.dispatch(markStoriesSynced(delta.server_time))
		}
	} catch (error) {
		console.warn("[storiesSync] Sync failed (will retry on next trigger):", error)
	} finally {
		syncInFlight = false
	}
}
